import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Copy, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GROUPS = [["all", "Tous les sponsors"], ["Or", "Or"], ["Argent", "Argent"], ["Bronze", "Bronze"]];
const SUBJECT = "Invitation à un événement partenaire";
const template = (club) =>
  `Bonjour,\n\n${club.name} a le plaisir de vous inviter à [nom de l'événement], le [date] à [lieu].\n\nMerci de nous confirmer votre présence avant le [date limite].\n\nSportivement,\n${club.manager_name || "[responsable partenariats]"}`;

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return; } catch { /* fallback below */ }
  const ta = document.createElement("textarea");
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  ta.remove();
}

export default function ContactSponsorsDialog({ open, onOpenChange, onDone }) {
  const { session } = useAuth();
  const club = session.club;
  const [list, setList] = useState([]);
  const [group, setGroup] = useState("all");
  const [subject, setSubject] = useState(SUBJECT);
  const [body, setBody] = useState("");
  const [asked, setAsked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGroup("all"); setSubject(SUBJECT); setBody(template(club)); setAsked(false);
    api.get("/sponsors").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
  }, [open, club]);

  const selected = useMemo(() => list.filter((s) => group === "all" || s.level === group), [list, group]);
  const withEmail = selected.filter((s) => s.email?.trim());
  const missing = selected.filter((s) => !s.email?.trim());
  const emails = withEmail.map((s) => s.email.trim());

  const openMail = () => {
    const q = `bcc=${encodeURIComponent(emails.join(","))}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = `mailto:?${q}`;
    setAsked(true);
  };
  const copy = async () => { await copyText(emails.join(", ")); toast.success("Adresses copiées : collez-les dans le champ Cci"); };
  const confirmSent = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sponsors/history-bulk", { sponsor_ids: withEmail.map((s) => s.id), subject });
      toast.success(`Invitation ajoutée à l'historique de ${data.count} sponsor${data.count > 1 ? "s" : ""}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[620px] max-h-[92vh] overflow-y-auto rounded-[14px]" data-testid="contact-sponsors-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Contacter mes sponsors</DialogTitle>
          <DialogDescription>Le message s'ouvre dans votre messagerie, avec les sponsors en copie cachée : aucun ne voit l'adresse des autres.</DialogDescription>
        </DialogHeader>
        {asked ? (
          <div className="py-2" data-testid="contact-confirm-step">
            <p className="font-display text-lg font-bold">Message envoyé ?</p>
            <p className="text-sm text-[#5B665F] mt-1">Si oui, « Invitation envoyée : {subject} » sera ajouté à l'historique des {emails.length} sponsor{emails.length > 1 ? "s" : ""} contacté{emails.length > 1 ? "s" : ""}.</p>
            <div className="flex flex-wrap gap-2 mt-5">
              <button className="btn-primary" onClick={confirmSent} disabled={busy} data-testid="contact-confirm-yes">Oui, message envoyé</button>
              <button className="btn-outline" onClick={() => setAsked(false)} data-testid="contact-confirm-no">Non</button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <span className="field-label">Destinataires</span>
              <div className="flex flex-wrap gap-2" role="radiogroup">
                {GROUPS.map(([k, label]) => (
                  <label key={k} className={`inline-flex items-center gap-2 h-9 px-3 rounded-full border text-sm font-semibold cursor-pointer transition-colors ${group === k ? "border-[#1E6B45] bg-[#DFF3E6] text-[#145A36]" : "border-[#D5D9D1] bg-white"}`}>
                    <input type="radio" name="group" className="accent-[#1E6B45]" checked={group === k} onChange={() => setGroup(k)} data-testid={`contact-group-${k}`} />{label}
                  </label>
                ))}
              </div>
              <p className="text-sm mt-3" data-testid="contact-selected-count"><b>{withEmail.length}</b> sponsor{withEmail.length > 1 ? "s" : ""} sélectionné{withEmail.length > 1 ? "s" : ""} avec un email</p>
              {missing.length > 0 && (
                <div className="mt-2 rounded-lg bg-[#FDEBD3] text-[#8A4B00] px-3 py-2 text-sm" data-testid="contact-missing-emails">
                  <p className="flex items-center gap-1.5 font-semibold"><AlertCircle size={14} /> Sans email renseigné :</p>
                  <ul className="mt-1 space-y-0.5">
                    {missing.map((s) => <li key={s.id}><Link to={`/sponsors/${s.id}`} className="underline underline-offset-2" onClick={() => onOpenChange(false)} data-testid={`contact-missing-${s.id}`}>{s.name}</Link> : compléter la fiche</li>)}
                  </ul>
                </div>
              )}
            </div>
            <label className="block"><span className="field-label">Objet</span><input className="field" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="contact-subject-input" /></label>
            <label className="block"><span className="field-label">Message</span><textarea className="field h-auto min-h-[200px] py-2 leading-relaxed" value={body} onChange={(e) => setBody(e.target.value)} data-testid="contact-body-input" /></label>
            <p className="text-xs text-[#5B665F]">Remplacez les éléments entre crochets avant l'envoi.</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button className="btn-primary" onClick={openMail} disabled={!emails.length || !subject.trim()} data-testid="contact-open-mail-button"><Mail size={16} /> Ouvrir dans ma messagerie</button>
              <button className="btn-outline" onClick={copy} disabled={!emails.length} data-testid="contact-copy-button"><Copy size={16} /> Copier les adresses</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
